
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ViewMode } from "./types";
import { Building, MapPin } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";

interface Company {
  id: string;
  name: string;
  city?: string;
  country?: string;
  logo_url?: string;
}

interface CompanySearchResultsProps {
  searchQuery: string;
  onResultClick: (id: string) => void;
  viewMode: ViewMode;
}

export function CompanySearchResults({ searchQuery, onResultClick, viewMode }: CompanySearchResultsProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const resultsPerPage = 5;

  useEffect(() => {
    // Reset to first page on new search
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const searchCompanies = async () => {
      if (!searchQuery.trim()) return;

      setIsLoading(true);
      
      try {
        // Get total count for pagination
        const { count, error: countError } = await supabase
          .from("companies")
          .select("*", { count: 'exact', head: true })
          .or(`name.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%,country.ilike.%${searchQuery}%`);
          
        if (countError) throw countError;
        setTotalCompanies(count || 0);
        
        // All search fields are now case-insensitive
        const { data, error } = await supabase
          .from("companies")
          .select("id, name, city, country, logo_url")
          .or(`name.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%,country.ilike.%${searchQuery}%`)
          .order("name", { ascending: true })
          .range((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage - 1);

        if (error) throw error;
        
        setCompanies(data || []);
      } catch (error) {
        console.error("Error searching companies:", error);
      } finally {
        setIsLoading(false);
      }
    };

    searchCompanies();
  }, [searchQuery, currentPage]);

  const totalPages = Math.ceil(totalCompanies / resultsPerPage);

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-500">
      <div className="animate-spin mr-2 h-5 w-5 border-2 border-digger-blue border-t-transparent rounded-full"></div>
      Searching companies...
    </div>;
  }

  if (companies.length === 0) {
    return <div className="flex flex-col items-center justify-center h-40 text-gray-500">
      <Building className="h-12 w-12 mb-2 text-gray-400" />
      <p>No companies found matching "<span className="font-medium">{searchQuery}</span>"</p>
    </div>;
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <Pagination className="my-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
          
          {[...Array(Math.min(3, totalPages))].map((_, i) => {
            const pageNumber = i + 1;
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  onClick={() => setCurrentPage(pageNumber)}
                  isActive={currentPage === pageNumber}
                  className="cursor-pointer"
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          
          {totalPages > 3 && (
            <PaginationItem>
              <PaginationLink
                onClick={() => setCurrentPage(totalPages)}
                isActive={currentPage === totalPages}
                className="cursor-pointer"
              >
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
          
          <PaginationItem>
            <PaginationNext 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {viewMode === "card" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {companies.map((company) => (
              <div
                key={company.id}
                className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors shadow-sm hover:shadow"
                onClick={() => onResultClick(company.id)}
              >
                <div className="flex items-center">
                  <div className="mr-3 w-12 h-12 flex-shrink-0">
                    {company.logo_url ? (
                      <img 
                        src={company.logo_url} 
                        alt={company.name} 
                        className="w-12 h-12 rounded object-contain bg-white border p-1"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentNode?.appendChild(
                            Object.assign(document.createElement('div'), {
                              className: 'w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-digger-blue',
                              innerHTML: '<span class="text-lg">🏢</span>'
                            })
                          );
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-digger-blue">
                        <Building className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-digger-blue">{company.name}</h3>
                    {(company.city || company.country) && (
                      <p className="text-gray-600 text-sm flex items-center mt-1">
                        <MapPin className="h-3.5 w-3.5 mr-1 inline-block" />
                        {[company.city, company.country].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {renderPagination()}
        </>
      ) : (
        <>
          <div className="overflow-hidden">
            <div className="grid grid-cols-12 bg-muted px-4 py-2.5 text-sm font-medium sticky top-0">
              <div className="col-span-1"></div>
              <div className="col-span-5">Company Name</div>
              <div className="col-span-6">Location</div>
            </div>
            
            <div>
              {companies.map((company) => (
                <div 
                  key={company.id} 
                  className="grid grid-cols-12 px-4 py-3 border-b hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onResultClick(company.id)}
                >
                  <div className="col-span-1">
                    {company.logo_url ? (
                      <img 
                        src={company.logo_url} 
                        alt={company.name} 
                        className="w-8 h-8 rounded object-contain border p-0.5 bg-white"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentNode?.appendChild(
                            Object.assign(document.createElement('div'), {
                              className: 'w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-digger-blue',
                              innerHTML: '<span class="text-lg">🏢</span>'
                            })
                          );
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-digger-blue">
                        <Building className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <div className="col-span-5 flex items-center">
                    <span className="font-medium">{company.name}</span>
                  </div>
                  <div className="col-span-6 flex items-center">
                    {(company.city || company.country) ? (
                      <>
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        {[company.city, company.country].filter(Boolean).join(", ")}
                      </>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
}
